import { centralizedCache } from '@graphql-api/CentralizedCache.js'
import { getCurrentToken } from '@graphql-api/index.js'
import {
  identifyUser,
  initAmplitude,
  resetAmplitudeUser
} from '@kestrix/analytics'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'

import { loginUser, verifyUser } from '../services/auth.js'
import type {
  AuthContext as typeAuthContext,
  LoginResponse,
  User
} from '../types/types.js'

export const AuthContext = createContext<typeAuthContext | null>(null)

/**
 * Custom hook to access authentication context.
 *
 * This hook provides access to the authentication state and methods.
 * It must be used within an AuthProvider component.
 *
 * @returns {Object} Authentication context containing user, token, loading state, and auth methods
 * @throws {Error} If used outside of AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Authentication context provider component.
 *
 * This component manages the global authentication state including:
 * - User information and authentication token
 * - Login/logout functionality
 * - Loading and error states
 * - Automatic token restoration from localStorage
 *
 */
export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(() => getCurrentToken())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const logout = useCallback(() => {
    localStorage.removeItem(import.meta.env.VITE_JWT_STORAGE_KEY || 'jwt_token')
    localStorage.removeItem('user')

    centralizedCache.clearSessionCache()
    resetAmplitudeUser()

    setToken(null)
    setUser(null)
    setError(null)
  }, [])

  useEffect(() => {
    initAmplitude()
  }, [])

  // Check for existing token on app load
  useEffect(() => {
    const storedToken = getCurrentToken()
    const storedUser = localStorage.getItem('user')

    const checkAuthentication = async (): Promise<void> => {
      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser) as User
          const verifyUserResponse = await verifyUser(parsedUser.id)

          if (verifyUserResponse.success) {
            setUser(parsedUser)
            identifyUser(parsedUser)
          } else {
            logout()
          }
        } catch (error) {
          console.error('Error parsing stored user data:', error)
          logout()
        } finally {
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    }

    void checkAuthentication()
  }, [logout])

  /**
   * Authenticates a user with the provided credentials.
   *
   * This function attempts to log in the user using the auth service.
   * On successful authentication, it stores the token and user data in localStorage
   * and updates the authentication state.
   */
  const login = async (
    username: string,
    password: string
  ): Promise<LoginResponse | void> => {
    setLoading(true)
    setError(null)

    try {
      const response = await loginUser(username, password)

      if (response.success === true && response.data) {
        const {
          user: userData,
          token: authToken,
          shouldChangePassword
        } = response.data

        // Store token and user data
        localStorage.setItem(
          import.meta.env.VITE_JWT_STORAGE_KEY || 'jwt_token',
          authToken
        )
        localStorage.setItem('user', JSON.stringify(userData))

        setToken(authToken)
        setUser(userData)
        identifyUser(userData)

        return { success: true, shouldChangePassword }
      } else if (response.success === false && response.error) {
        setError(response.error || 'Login failed')
        return { success: false, error: response.error }
      }
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = error.message || 'An unexpected error occurred'
        setError(errorMessage)
        return { success: false, error: errorMessage }
      }
    } finally {
      setLoading(false)
    }
  }

  /**
   * Clears any authentication error messages.
   *
   * This function resets the error state to null, typically called
   * when starting a new authentication attempt.
   */
  const clearError = () => {
    setError(null)
  }

  const value = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    clearError,
    isAuthenticated: !!token
  }

  return <AuthContext value={value}>{children}</AuthContext>
}
